import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useMediaQuery } from '@vueuse/core'
// @ts-expect-error importing untyped JS module into TypeScript
import { hasAccessToRouteName } from '@/router/RouterAccessGuard'
import { useWhatsNewStore } from '@/stores/whatsNew'
import { resolveUpsellDetails } from '@/composables/useUpselling'
import { ROUTE_UPSELLING_DETAILS, type RouteUpSellingDetail } from '@/utils/upselling'
import { isIntercomAvailable } from '@/utils/intercom'
import { chooseCta, isListed, isNews, isUpsell, newestFirst, type ResolvedWhatsNewCta, type WhatsNewContext } from './eligibility'
import { WHATS_NEW_ITEMS, type WhatsNewAction, type WhatsNewItem } from './whats-new-items'

export type { ResolvedWhatsNewCta }

/**
 * The What's New catalog as it applies to the signed-in person: what the panel lists, what
 * counts as unread, and what the banner should show.
 */
export function useWhatsNew (items: WhatsNewItem[] = WHATS_NEW_ITEMS) {
  const router = useRouter()
  const store = useStore()
  const whatsNewStore = useWhatsNewStore()

  // The desktop sidebar, and with it Customize mode, starts at Tailwind's md breakpoint. The
  // mobile drawer has no Customize mode.
  const hasDesktopSidebar = useMediaQuery('(min-width: 768px)')

  // `now` is read when these recompute, not ticked: an item crossing a date boundary mid-session
  // shows up on the next reload, which is soon enough for news.
  const context = computed<WhatsNewContext>(() => ({
    now: new Date(),
    userCreated: store.state.userCreated,
    canAccessRoute: (name: string) => hasAccessToRouteName(router, store, name),
    features: store.state.features ?? [],
    isAccountOwner: store.getters.isAccountOwner,
    getters: store.getters
  }))

  /** Everything the panel lists, newest first. */
  const listed = computed(() => newestFirst(items.filter(item => isListed(item, context.value))))

  /** News to this person that they have not seen. Drives the Help menu dot and the banner. */
  const unread = computed(() =>
    listed.value.filter(item => isNews(item, context.value) && !whatsNewStore.isSeen(item.id))
  )

  /** What the banner shows: the unread items, when it is a good moment to interrupt at all. */
  const bannerItems = computed(() => (whatsNewStore.canAutoShow ? unread.value : []))

  function isActionAvailable (action: WhatsNewAction): boolean {
    switch (action) {
      case 'customizeSidebar':
        return hasDesktopSidebar.value
      case 'openSupportChat':
        return isIntercomAvailable()
    }
  }

  /** A call to action that would do nothing here is not offered at all. */
  function ctaFor (item: WhatsNewItem): ResolvedWhatsNewCta | null {
    return chooseCta(item, context.value, isActionAvailable)
  }

  /** The plan pitch and upgrade button for the upsell version; null for everyone who has it. */
  function upsellFor (item: WhatsNewItem): RouteUpSellingDetail | null {
    if (!isUpsell(item, context.value)) {
      return null
    }
    const base = ROUTE_UPSELLING_DETAILS.find(detail => detail.feature === item.upsellFeature) ?? {
      feature: item.upsellFeature ?? '',
      title: item.title,
      additionalDetails: 'This feature is available with the <i>Premium Plan.</i>'
    }
    return resolveUpsellDetails(base, {
      isAccountOwner: store.getters.isAccountOwner,
      isByxbiOrg: store.getters.isByxbiOrg,
      upgrade: () => void router.push({ name: 'billing-subscription' })
    })
  }

  return {
    listed,
    unread,
    bannerItems,
    ctaFor,
    upsellFor
  }
}
